import { useState } from 'react';

import { ArrowLeft, Upload } from 'lucide-react';

import { FileUpload } from '@/components/FileUpload';
import { SelectedFilesList } from '@/components/SelectedFilesList';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';

import { DocumentsUploadPipeline } from './DocumentsUploadPipeline';

interface IataBinderDocumentsUploadProps {
    onBack: () => void;
}

export function IataBinderDocumentsUpload({
    onBack,
}: IataBinderDocumentsUploadProps) {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [showPipeline, setShowPipeline] = useState(false);

    const accept = '.pdf,.xlsx,.xls,.png,.jpg,.jpeg,.tif,.tiff';

    const handleUpload = () => {
        if (selectedFiles.length > 0) {
            setShowPipeline(true);
        }
    };

    const handleBackToUpload = () => {
        setShowPipeline(false);
        setSelectedFiles([]);
    };

    // Show pipeline view after file selection and upload click
    if (showPipeline) {
        return (
            <div
                className="flex-1 min-h-full bg-muted/50"
                data-testid="IataBinderDocumentsUpload"
            >
                <div className="bg-white shadow-xs">
                    <div className="px-8 py-6 flex flex-col gap-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={handleBackToUpload}
                                    className="text-muted-foreground hover:text-foreground -ml-2"
                                >
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Back to Upload
                                </Button>
                            </div>
                        </div>
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900">
                                Document Ingestion Progress
                            </h1>
                            <p className="text-xs text-slate-500 mt-0.5">
                                Processing uploaded documents
                            </p>
                        </div>
                    </div>
                </div>
                <DocumentsUploadPipeline
                    fileName={selectedFiles[0]?.name}
                    fileSize={
                        selectedFiles[0]
                            ? `${(selectedFiles[0].size / (1024 * 1024)).toFixed(1)} MB`
                            : undefined
                    }
                />
            </div>
        );
    }

    // Default: File upload form view
    return (
        <div
            className="flex-1 min-h-full bg-muted/50"
            data-testid="IataBinderDocumentsUpload"
        >
            <div className="bg-white shadow-xs">
                <div className="px-8 py-6 flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={onBack}
                                className="text-muted-foreground hover:text-foreground -ml-2"
                            >
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Back to Project
                            </Button>
                        </div>
                    </div>
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            Upload Documents
                        </h1>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Upload technical documentation to the IATA Binder
                        </p>
                    </div>
                </div>
            </div>

            <div className="p-8 max-w-2xl mx-auto">
                <Card data-testid="IataBinderFileUpload">
                    <CardHeader>
                        <CardTitle>Select File</CardTitle>
                        <CardDescription>
                            Upload relevant aviation documents, including
                            manuals, certificates, and technical records.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <FileUpload
                            multiple
                            selectedFiles={selectedFiles}
                            onFilesChange={setSelectedFiles}
                            accept={accept}
                        />
                        {selectedFiles.length > 0 && (
                            <SelectedFilesList
                                selectedFiles={selectedFiles}
                                onFilesChange={setSelectedFiles}
                            />
                        )}
                        <Button
                            onClick={handleUpload}
                            disabled={selectedFiles.length === 0}
                            className="w-full"
                        >
                            <Upload className="h-4 w-4 mr-2" />
                            Upload {selectedFiles.length > 0 && `(${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''})`}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
